import { Injectable } from '@angular/core';

import { BehaviorSubject, Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { IntegrationMethodDefault } from '@models/integration-method/integration-method-default';

import { HttpService } from '@services/http.service';
import { EndpointParams } from '@models/utils/endpoint-params';
import { Card } from '@models/integration-method/card/card';

@Injectable({
    providedIn: 'root'
})
export class CardsService extends HttpService {
    private readonly defaultEndpointParams: EndpointParams = { app: 'payments', version: 1 };

    private readonly cardsSubject = new BehaviorSubject<Card[]>([]);
    private readonly defaultIdSubject = new BehaviorSubject<string>('');
    private readonly loadingSubject = new BehaviorSubject<boolean>(false);

    private readonly dataStore: {
        cards: Card[];
        defaultId: string;
        actualDefaultId: string;
    } = {
            cards: [],
            defaultId: '',
            actualDefaultId: ''
        };

    readonly cards: Observable<Card[]> = this.cardsSubject.asObservable();
    readonly default: Observable<string> = this.defaultIdSubject.asObservable();
    readonly loading: Observable<boolean> = this.loadingSubject.asObservable();

    listCards(): Observable<Card[]> {
        return this.get('cards/member', this.defaultEndpointParams);
    }

    list(): void {
        this.setLoading(true);
        this.listCards().subscribe(cards => {
            this.dataStore.cards = cards;
            const def = this.dataStore.cards.find(c => c.defaultPaymentMethod)?.id ?? '';
            this.dataStore.defaultId = def;
            this.dataStore.actualDefaultId = def;
            this.next();
            this.nextDefault();
            this.setLoading(false);
        });
    }

    getDefaultCardId(): Observable<string> {
        return this.cards.pipe(map((list: Card[]) => list.find(c => c.defaultPaymentMethod)?.id ?? ''));
    }

    cancelDefault(): void {
        this.dataStore.defaultId = this.dataStore.actualDefaultId;
        this.nextDefault();
    }

    setDefault(integrationMethodDefault: IntegrationMethodDefault): Observable<boolean> {
        return new Observable<boolean>((observable) => {
            this.put('member/paymentmethod/default', this.defaultEndpointParams, integrationMethodDefault).subscribe(
                () => {
                    this.dataStore.actualDefaultId = integrationMethodDefault.idPaymentMethod;
                    this.dataStore.defaultId = integrationMethodDefault.idPaymentMethod;
                    this.nextDefault();
                    observable.next(true);
                },
                () => {
                    this.cancelDefault();
                    observable.next(false);
                }
            );
        });
    }

    removeCard(idCard: string): Observable<boolean> {
        this.setLoading(true);
        return new Observable<boolean>((observable) => {
            this.delete(`cards/member/${idCard}`, this.defaultEndpointParams).subscribe(
                () => {
                    observable.next(true);
                    this.list();
                },
                () => {
                    observable.next(false);
                }
            );
        });
    }

    removeCardCheck(idCard: string): Observable<boolean> {
        return new Observable<boolean>((observable) => {
            this.delete(`cards/member/${idCard}/check`, this.defaultEndpointParams, this.noShowErrorNotification).subscribe(
                () => {
                    observable.next(true);
                },
                (error) => {
                    observable.error(error);
                }
            );
        });
    }

    private next(): void {
        this.cardsSubject.next([...this.dataStore.cards]);
    }

    private nextDefault(): void {
        this.defaultIdSubject.next(this.dataStore.defaultId);
    }

    private setLoading(value: boolean): void {
        this.loadingSubject.next(value);
    }
}
